import { useEffect, useState } from 'react';
import { useAuth } from './useAuth';

/**
 * Which organization a telephony page is acting on, and whether the user may
 * change it.
 *
 * <h3>The bug this exists to stop coming back</h3>
 * Five pages (DND Lists, VIP Lists, Business Hours, OBD Campaigns, Campaign
 * Management) each carried the same copy-pasted pair:
 *
 *   const showOrgFilter = ['master_admin','super_admin','service_admin','service_user']
 *       .includes(currentRole);
 *   const [selectedOrgId, setSelectedOrgId] = useState('');
 *
 * `setSelectedOrgId` had exactly ONE caller, inside the `{showOrgFilter && ...}`
 * block. So for every role NOT in that list — which is precisely
 * `operator_admin`, the business admin — the selector never rendered, the setter
 * was unreachable, and `selectedOrgId` stayed '' forever. The page then showed an
 * empty list (the load is guarded on it), a permanently disabled Add button
 * (`disabled={!selectedOrgId}`), and no explanation, because the "choose a
 * business" hint was ALSO gated on showOrgFilter.
 *
 * The result looked like a working page belonging to somebody else. It is why
 * `dnd_entries` had 0 rows across all 47 organizations — read as "nobody wants
 * this feature" when it was "nobody could use it".
 *
 * <h3>Why the id is adopted in an effect and not just in useState</h3>
 * `currentOrganization` is loaded asynchronously by AuthContext, so it is null on
 * the first render. A useState initialiser alone would capture that null and the
 * page would stay broken exactly as before — the same bug wearing a hook.
 *
 * <h3>Why every hidden-filter role gets the id, not just business admins</h3>
 * "You act on your own organization" is the honest scoping rule. If the server
 * then refuses a role, the user sees a real error, which is better than an empty
 * list that quietly implies there is nothing there.
 */
const ROLES_THAT_MAY_CHOOSE_AN_ORG = [
  'master_admin',
  'super_admin',
  'service_admin',
  'service_user',
];

export function useScopedOrgId() {
  const { currentOrganization } = useAuth();
  const currentRole = currentOrganization?.role?.toLowerCase();
  const showOrgFilter = ROLES_THAT_MAY_CHOOSE_AN_ORG.includes(currentRole || '');

  const [selectedOrgId, setSelectedOrgId] = useState<string>('');

  useEffect(() => {
    // Only when the selector is hidden. A provider-level user who cleared the
    // filter on purpose must not have their choice overwritten on the next
    // render — OrganizationFilter already defaults itself when it is shown.
    if (!showOrgFilter && currentOrganization?.id) {
      setSelectedOrgId((existing) => existing || currentOrganization.id);
    }
  }, [showOrgFilter, currentOrganization?.id]);

  return { selectedOrgId, setSelectedOrgId, showOrgFilter };
}
